import React from 'react';
import { BarChart3, ArrowUpDown, TrendingUp, TrendingDown } from 'lucide-react';

export default function PortfolioMockup({ size = 'sm' }) {
  const isLg = size === 'lg';

  const portfolioData = [
    {
      address: '1428 Elm St',
      zip: '75001',
      purchase: '$185K',
      sale: '$295K',
      profit: '$47.5K',
      roi: '22.3%',
      positive: true,
    },
    {
      address: '742 Evergreen',
      zip: '85001',
      purchase: '$210K',
      sale: '$340K',
      profit: '$62.0K',
      roi: '24.8%',
      positive: true,
    },
    {
      address: '221B Baker St',
      zip: '30301',
      purchase: '$155K',
      sale: '$198K',
      profit: '-$3.2K',
      roi: '-1.8%',
      positive: false,
    },
  ];

  const summaryStats = [
    { label: 'Total Profit', value: '$106.3K', color: 'text-emerald-400' },
    { label: 'Avg ROI', value: '15.1%', color: 'text-blue-400' },
    { label: 'Avg Rehab/SF', value: '$28', color: 'text-violet-400' },
    { label: 'Completed', value: '12', color: 'text-amber-400' },
  ];

  return (
    <div className={`w-full ${isLg ? 'p-4' : 'p-3'} bg-slate-800/80 rounded-xl`}>
      {/* Mini header */}
      <div className="flex items-center gap-2 mb-3">
        <div className="w-5 h-5 rounded bg-cyan-500/20 flex items-center justify-center">
          <BarChart3 size={11} className="text-cyan-400" />
        </div>
        <span className={`${isLg ? 'text-xs' : 'text-[10px]'} font-semibold text-white`}>
          Portfolio Analytics
        </span>
      </div>

      {/* Summary stats */}
      {isLg && (
        <div className="grid grid-cols-4 gap-2 mb-3">
          {summaryStats.map((s, i) => (
            <div key={i} className="bg-slate-700/50 rounded-lg px-2 py-1.5 text-center">
              <p className="text-[7px] text-slate-500 uppercase tracking-wider">{s.label}</p>
              <p className={`text-sm font-extrabold ${s.color}`}>{s.value}</p>
            </div>
          ))}
        </div>
      )}

      {/* Mini table */}
      <div className="bg-slate-700/30 rounded-lg overflow-hidden">
        {/* Table header */}
        <div
          className={`grid ${isLg ? 'grid-cols-6' : 'grid-cols-4'} gap-1 px-2 py-1.5 bg-slate-700/50 border-b border-white/[0.05]`}
        >
          <div className="flex items-center gap-0.5">
            <span className="text-[7px] text-slate-500 font-semibold uppercase">Property</span>
            <ArrowUpDown size={7} className="text-slate-600" />
          </div>
          {isLg && (
            <span className="text-[7px] text-slate-500 font-semibold uppercase">Purchase</span>
          )}
          {isLg && <span className="text-[7px] text-slate-500 font-semibold uppercase">Sale</span>}
          <span className="text-[7px] text-slate-500 font-semibold uppercase">Profit</span>
          <span className="text-[7px] text-slate-500 font-semibold uppercase">ROI</span>
          <span className="text-[7px] text-slate-500 font-semibold uppercase text-right">
            Trend
          </span>
        </div>

        {/* Table rows */}
        {portfolioData.slice(0, isLg ? 3 : 2).map((row, i) => (
          <div
            key={i}
            className={`grid ${isLg ? 'grid-cols-6' : 'grid-cols-4'} gap-1 px-2 py-1.5 border-b border-white/[0.03] last:border-0`}
          >
            <div>
              <p
                className={`${isLg ? 'text-[10px]' : 'text-[8px]'} font-semibold text-white truncate`}
              >
                {row.address}
              </p>
              {isLg && <p className="text-[7px] text-slate-600">{row.zip}</p>}
            </div>
            {isLg && <span className="text-[9px] text-slate-400 self-center">{row.purchase}</span>}
            {isLg && <span className="text-[9px] text-slate-400 self-center">{row.sale}</span>}
            <span
              className={`text-[9px] font-bold self-center ${row.positive ? 'text-emerald-400' : 'text-red-400'}`}
            >
              {row.profit}
            </span>
            <span
              className={`text-[9px] font-bold self-center ${row.positive ? 'text-blue-400' : 'text-red-400'}`}
            >
              {row.roi}
            </span>
            <div className="flex justify-end self-center">
              {row.positive ? (
                <TrendingUp size={10} className="text-emerald-400" />
              ) : (
                <TrendingDown size={10} className="text-red-400" />
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Zip code summary (lg only) */}
      {isLg && (
        <div className="mt-3 bg-slate-700/30 rounded-lg p-3">
          <p className="text-[9px] text-slate-500 uppercase tracking-wider font-semibold mb-2">
            ROI by Zip Code
          </p>
          <div className="space-y-1.5">
            {[
              { zip: '75001 (Dallas)', roi: 22.3, color: 'bg-emerald-500' },
              { zip: '85001 (Phoenix)', roi: 24.8, color: 'bg-blue-500' },
              { zip: '30301 (Atlanta)', roi: -1.8, color: 'bg-red-500' },
            ].map((z, i) => (
              <div key={i} className="flex items-center gap-2">
                <span className="text-[9px] text-slate-400 w-28">{z.zip}</span>
                <div className="flex-1 h-1.5 bg-slate-700/50 rounded-full overflow-hidden">
                  <div
                    className={`h-full ${z.color} rounded-full`}
                    style={{ width: `${Math.max(Math.abs(z.roi) * 3, 5)}%` }}
                  />
                </div>
                <span
                  className={`text-[9px] font-bold w-10 text-right ${z.roi > 0 ? 'text-emerald-400' : 'text-red-400'}`}
                >
                  {z.roi}%
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
